import type {
  CustomLayerInterface,
  CustomRenderMethodInput,
  Map as MlMap,
} from "maplibre-gl";
import type { LngLat } from "../types";
import type { ImageQuad } from "./imageQuad";

export type MercatorPoint = [number, number, number];

export interface RoofImage {
  quad: ImageQuad;
  src: string;
  opacity: number;
  altitude: number;
  hoverAltitude: number;
}

const EARTH_CIRCUMFERENCE_METERS = 2 * Math.PI * 6371008.8;
const FADE_IN_DURATION_MS = 200;
const IDLE_TEXTURES_KEPT = 16;
const FLOATS_PER_VERTEX = 5;

export function mercatorPoint([lng, lat]: LngLat, altitude: number): MercatorPoint {
  const x = (180 + lng) / 360;
  const y = (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
  const z = altitude / (EARTH_CIRCUMFERENCE_METERS * Math.cos((lat * Math.PI) / 180));
  return [x, y, z];
}

export function roofVertices(quad: ImageQuad, altitude: number): { origin: MercatorPoint; vertices: Float32Array } {
  const [topLeft, topRight, bottomRight, bottomLeft] = quad.map((corner) => mercatorPoint(corner, altitude));
  const origin = topLeft!;
  const strip: [MercatorPoint, number, number][] = [
    [topLeft!, 0, 0],
    [topRight!, 1, 0],
    [bottomLeft!, 0, 1],
    [bottomRight!, 1, 1],
  ];
  const vertices = new Float32Array(strip.length * FLOATS_PER_VERTEX);
  strip.forEach(([[x, y, z], u, v], index) => {
    vertices.set([x - origin[0], y - origin[1], z - origin[2], u, v], index * FLOATS_PER_VERTEX);
  });
  return { origin, vertices };
}

export function translatedMatrix(matrix: ArrayLike<number>, [x, y, z]: MercatorPoint): Float32Array {
  const translated = Float32Array.from(matrix);
  for (let row = 0; row < 4; row++) {
    translated[12 + row] = matrix[row]! * x + matrix[4 + row]! * y + matrix[8 + row]! * z + matrix[12 + row]!;
  }
  return translated;
}

const VERTEX_SHADER = `
attribute vec3 a_position;
attribute vec2 a_uv;
uniform mat4 u_matrix;
varying vec2 v_uv;
void main() {
  v_uv = a_uv;
  gl_Position = u_matrix * vec4(a_position, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
uniform sampler2D u_image;
uniform float u_opacity;
varying vec2 v_uv;
void main() {
  vec4 color = texture2D(u_image, v_uv);
  gl_FragColor = vec4(color.rgb * color.a, color.a) * u_opacity;
}`;

interface RoofTexture {
  texture: WebGLTexture;
  shownAt: number;
}

interface GlResources {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  position: number;
  uv: number;
  matrix: WebGLUniformLocation;
  opacity: WebGLUniformLocation;
  image: WebGLUniformLocation;
}

type TextureImage = HTMLImageElement | ImageBitmap;

export class RoofImageLayer implements CustomLayerInterface {
  readonly type = "custom";
  readonly renderingMode = "3d";

  private map: MlMap | null = null;
  private resources: GlResources | null = null;
  private readonly roofs = new Map<string, RoofImage>();
  private readonly textures = new Map<string, RoofTexture>();
  private readonly loading = new Set<string>();
  private readonly loaded = new Map<string, TextureImage>();

  constructor(
    readonly id: string,
    private readonly isHovered: (shapeId: string) => boolean,
  ) {}

  get size(): number {
    return this.roofs.size;
  }

  has(shapeId: string): boolean {
    return this.roofs.has(shapeId);
  }

  shapeIds(): string[] {
    return [...this.roofs.keys()];
  }

  set(shapeId: string, roof: RoofImage): void {
    this.roofs.set(shapeId, roof);
    this.load(roof.src);
    this.map?.triggerRepaint();
  }

  delete(shapeId: string): void {
    if (!this.roofs.delete(shapeId)) return;
    this.map?.triggerRepaint();
  }

  onAdd(map: MlMap): void {
    this.map = map;
  }

  onRemove(_map: MlMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    for (const { texture } of this.textures.values()) gl.deleteTexture(texture);
    if (this.resources) {
      gl.deleteProgram(this.resources.program);
      gl.deleteBuffer(this.resources.buffer);
    }
    this.textures.clear();
    this.loaded.clear();
    this.loading.clear();
    this.roofs.clear();
    this.resources = null;
    this.map = null;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    const matrix = options.defaultProjectionData.mainMatrix;
    this.uploadLoadedTextures(gl);
    this.releaseIdleTextures(gl);
    if (this.roofs.size === 0) return;

    const resources = this.resources ?? (this.resources = createResources(gl));
    if (!resources) return;

    gl.useProgram(resources.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
    gl.enableVertexAttribArray(resources.position);
    gl.enableVertexAttribArray(resources.uv);
    gl.vertexAttribPointer(resources.position, 3, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, 0);
    gl.vertexAttribPointer(resources.uv, 2, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, 3 * 4);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(resources.image, 0);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(-1, -1);

    const now = performance.now();
    let fading = false;
    for (const [shapeId, roof] of this.roofs) {
      const texture = this.textures.get(roof.src);
      if (!texture) continue;
      const fade = Math.min(1, (now - texture.shownAt) / FADE_IN_DURATION_MS);
      if (fade < 1) fading = true;

      const altitude = this.isHovered(shapeId) ? roof.hoverAltitude : roof.altitude;
      const { origin, vertices } = roofVertices(roof.quad, altitude);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
      gl.uniformMatrix4fv(resources.matrix, false, translatedMatrix(matrix, origin));
      gl.uniform1f(resources.opacity, roof.opacity * fade);
      gl.bindTexture(gl.TEXTURE_2D, texture.texture);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    gl.disable(gl.POLYGON_OFFSET_FILL);
    gl.disableVertexAttribArray(resources.position);
    gl.disableVertexAttribArray(resources.uv);
    if (fading) this.map?.triggerRepaint();
  }

  private load(src: string): void {
    if (!this.map || this.textures.has(src) || this.loaded.has(src) || this.loading.has(src)) return;
    this.loading.add(src);
    const map = this.map;
    map.loadImage(src).then(
      (response) => {
        if (this.map !== map || !this.loading.delete(src)) return;
        this.loaded.set(src, response.data);
        map.triggerRepaint();
      },
      () => {
        this.loading.delete(src);
      },
    );
  }

  private uploadLoadedTextures(gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    for (const [src, image] of this.loaded) {
      const texture = gl.createTexture();
      if (!texture) continue;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      if (gl instanceof WebGL2RenderingContext) {
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      } else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      }
      this.textures.set(src, { texture, shownAt: performance.now() });
    }
    this.loaded.clear();
  }

  private releaseIdleTextures(gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    const used = new Set([...this.roofs.values()].map((roof) => roof.src));
    const idle = [...this.textures.keys()].filter((src) => !used.has(src));
    for (const src of idle.slice(0, Math.max(0, idle.length - IDLE_TEXTURES_KEPT))) {
      gl.deleteTexture(this.textures.get(src)!.texture);
      this.textures.delete(src);
    }
  }
}

function createResources(gl: WebGLRenderingContext | WebGL2RenderingContext): GlResources | null {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  const buffer = gl.createBuffer();
  if (!vertexShader || !fragmentShader || !program || !buffer) return null;

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;

  const matrix = gl.getUniformLocation(program, "u_matrix");
  const opacity = gl.getUniformLocation(program, "u_opacity");
  const image = gl.getUniformLocation(program, "u_image");
  if (!matrix || !opacity || !image) return null;

  return {
    program,
    buffer,
    position: gl.getAttribLocation(program, "a_position"),
    uv: gl.getAttribLocation(program, "a_uv"),
    matrix,
    opacity,
    image,
  };
}

function compileShader(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
}
